// src/components/Sidebar.js
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";

export default function Sidebar() {
  const { user, logout } = useAuth();

  const [premium, setPremium] = useState(false);
  const [username, setUsername] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Load Firestore info (premium + name)
  useEffect(() => {
    if (!user) return;

    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setPremium(!!data.premium);
        setUsername(data.displayName || user.email.split("@")[0]);
      }
    });

    return () => unsub();
  }, [user]);

  // Toggle body class for mobile slide-in
  useEffect(() => {
    if (sidebarOpen) document.body.classList.add("sidebar-open");
    else document.body.classList.remove("sidebar-open");
  }, [sidebarOpen]);

  return (
    <>
      {/* MOBILE TOGGLE BUTTON */}
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        ☰
      </button>

      {/* MOBILE BACKDROP */}
      <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />

      {/* SIDEBAR */}
      <div className="sidebar">
        {/* LOGO */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "2rem",
            paddingLeft: "0.3rem",
          }}
        >
          <img
            src="/logo192.png"
            alt="logo"
            style={{ width: "32px", height: "32px", borderRadius: "6px" }}
          />
          <span style={{ fontSize: "1.15rem", fontWeight: 600 }}>
            ReflectRise
          </span>
        </div>

        <nav>
          <ul style={{ listStyle: "none", padding: 0 }}>
            <SidebarLink to="/home" label="Home" />
            <SidebarLink to="/dashboard" label="Dashboard" />
            <SidebarLink to="/journal" label="Journal" />
            <SidebarLink to="/goals" label="Goals" />

            {/* ✅ NEW: Planner (Premium Feature) */}
            <SidebarLink
              to="/planner"
              label={premium ? "Planner" : "Planner"}
            />

            <SidebarLink to="/streaks" label="Streaks" />
            <SidebarLink to="/updates" label="What's New" />
            <SidebarLink to="/premium" label="Premium" />

            {premium && (
              <SidebarLink
                to="/manage-subscription"
                label="Manage Subscription"
              />
            )}

            {/* About Page */}
            <SidebarLink to="/about" label="About" />

            {/* Policies */}
            <li style={{ marginBottom: "0.75rem" }}>
              <a
                href="https://reflectrise.github.io/reflectrise-privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-link"
                style={{
                  padding: "8px 12px",
                  display: "block",
                  borderRadius: "8px",
                  textDecoration: "none",
                }}
              >
                Policies
              </a>
            </li>

            {/* Rate ReflectRise */}
            <li style={{ marginBottom: "0.75rem" }}>
              <a
                href="https://g.page/r/CWCnQS1DNY6VEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-link"
                style={{
                  padding: "8px 12px",
                  display: "block",
                  borderRadius: "8px",
                  textDecoration: "none",
                }}
              >
                Rate ReflectRise
              </a>
            </li>

            <SidebarLink to="/profile" label="Profile" />
          </ul>
        </nav>

        {/* FOOTER AREA */}
        <div style={{ marginTop: "auto", paddingTop: "1.5rem" }}>
          {user && (
            <div style={{ marginBottom: "1rem" }}>
              <span className="sidebar-username">{username}</span>
              {premium ? (
                <span className="sidebar-premium-badge">Premium</span>
              ) : (
                <span className="sidebar-free-badge">Free</span>
              )}
            </div>
          )}

          <button className="logout-btn" onClick={logout}>
            ⛔ Sign Out
          </button>
        </div>
      </div>
    </>
  );
}

function SidebarLink({ to, label }) {
  return (
    <li style={{ marginBottom: "0.75rem" }}>
      <NavLink
        to={to}
        className={({ isActive }) =>
          isActive ? "sidebar-link active" : "sidebar-link"
        }
        style={{
          padding: "8px 12px",
          display: "block",
          borderRadius: "8px",
          textDecoration: "none",
        }}
      >
        {label}
      </NavLink>
    </li>
  );
}
